import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import useQuery from "hooks/useQuery";
import { Form } from "react-bootstrap";
import { ReactComponent as IconTrash } from 'bootstrap-icons/icons/trash.svg'
import {ReactComponent as IconPencilSquare} from 'bootstrap-icons/icons/pencil-square.svg'

import useUpload from "hooks/useUpload";
import useMutation from "hooks/useMutation";

const Notes = ({ consumer, resource, onClose }) => {
  const { t } = useTranslation();
  const upload = useUpload();
  const fileInputRef = useRef(null);
  const [consumerNotes, setConsumerNotes] = useState([]);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [isNewNoteFocused, setIsNewNoteFocused] = useState(false);
  const [currentNote, setCurrentNote] = useState({
    date: new Date().toISOString().split('T')[0],
    content: '',
    files: [],
    createdByName: resource?.label
  });

  // Crear nota
  const [createNote, createMutation] = useMutation(window._l(`/${consumer?.id}/notes`).substr(1), {
    onSuccess: (data) => {
      // Bug real (auditoría 2026-08-14): faltaba el spread de `prev` —
      // en vez de agregar la nota a la lista, la lista entera quedaba
      // anidada como el primer elemento de un array de 2 (`[prev, nota]`),
      // corrompiendo `consumerNotes` en cada nota nueva agregada por
      // cualquier usuario del equipo.
      setConsumerNotes(prev => {
        return [...(Array.isArray(prev) ? prev : []), {
          ...data
        }
        ];
      });

      setCurrentNote({
        date: new Date().toISOString().split('T')[0],
        content: '',
        files: [],
        createdByName: resource?.label
      });
    },
    // Bug real (auditoría 2026-08-14): sin onError la nota parecía perderse
    // en silencio — el usuario no se enteraba de que el guardado había
    // fallado (ni error visible, ni la nota se agregaba a la lista).
    onError: (error) => {
      window.notify({
        message: error.message,
        type: "danger",
        title: "Error",
      });
    },
  });

  // Actualizar nota existente
  const [updateNote, updateMutation] = useMutation(window._l(`/${consumer?.id}/notes`).substr(1), {
    onSuccess: (data) => {
      setConsumerNotes(prev => {
        try {
          const list = Array.isArray(prev) ? [...prev] : [];
          const idx = list.findIndex(n => n.id === data.id);
          if (idx >= 0) {
            list[idx] = { ...list[idx], ...data };
          } else {
            list.push(data);
          }
          return list;
        } catch {
          return prev;
        }
      });
      setEditingNoteId(null);
      setEditContent("");
    },
    onError: (error) => {
      window.notify({
        message: error.message,
        type: "danger",
        title: "Error",
      });
    },
  });

  const notesQuery = useQuery(window._l(`/${consumer?.id}/notes`).substr(1), {
    enabled: !!consumer?.id,
  });


  const handleOnChange = ({ target: { name, value } }) => {
    setCurrentNote(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = () => {
    createNote({
      ...currentNote,
      consumerID: consumer.id,
    });
  }

  const startEditing = (note) => {
    setEditingNoteId(note.id);
    setEditContent(note.content || "");
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditContent("");
  };

  const saveEditing = (note) => {
    const payload = { ...note, content: editContent, consumerID: consumer.id };
    updateNote(payload);
  };

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const files = e.target.files;
    const urls = await upload(files);
    // Bug real (auditoría 2026-08-14): useUpload() devuelve null si falla
    // (ya notifica el error con window.notify) — sin este guard, urls.map
    // explotaba con "Cannot read properties of null (reading 'map')".
    if (!urls) return;

    const filesArrays = urls.map((url, index) => ({
      url,
      name: files[index].name,
      type: files[index].type,
      size: files[index].size
    }));

    setCurrentNote(prev => ({
      ...prev,
      files: prev.files ? prev.files.concat(filesArrays) : filesArrays
    }));
  };

  const deleteNoteFile = (index) => {
    setCurrentNote(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  }

  useEffect(() => {
    if (notesQuery.data) {
      setConsumerNotes(notesQuery.data);
    }
  }, [notesQuery.data]);

  return (
    <div className="p-2 pe-0 font-nunito d-flex flex-column overflow-auto" style={{ width: '100%', maxHeight: '100%' }}>
      <div className="d-flex justify-content-between">
        <h4
          className="mb-0"
          style={{ fontWeight: 700, color: '#13629B' }}
        >
          {t('consumer_notes', "Notas")}
        </h4>
        {onClose && (
          <button
            type="button"
            className="btn-close float-end small"
            onClick={onClose}
          />
        )}
      </div>

      <p className="small mb-1">
        {t('notes_description', 'Estas notas no figurarán en la Historia Clínica, y serán visibles para todo el equipo.')}
      </p>


      <Form
        className="p-2 rounded shadow-sm bg-white"
      >
        <Form.Group className="my-2">
          <textarea
            className="form-control"
            name='content'
            value={currentNote.content}
            onChange={handleOnChange}
            style={{ resize: "none" }}
            rows={isNewNoteFocused ? 4 : 1}
            placeholder={t('add_note', 'Agregar nota...')}
            onFocus={() => setIsNewNoteFocused(true)}
            onBlur={() => setIsNewNoteFocused(false)}
          />
        </Form.Group>

        <Form.Group>
          {currentNote.files?.length > 0 && (
            <>
              <div className="d-flex flex-wrap text-secondary align-items-center">
                <span>Adjuntado: </span>
                {currentNote.files.map((f, i) => (
                  <span
                    key={i}
                    className="ms-1 my-1 d-inline-flex align-items-center gap-2 px-1 py-0 border rounded small"
                    style={{
                      backgroundColor: '#EEEFEF',
                      maxWidth: '240px',
                      minWidth: 0,
                    }}
                  >
                    <span
                      className="text-truncate font-italic"
                    >📄{f.name}</span>
                    <button
                      type="button"
                      className="border-0 bg-transparent p-0 flex-shrink-0"
                      onClick={() => deleteNoteFile(i)}
                    >
                      <IconTrash />
                    </button>
                  </span>
                ))}
              </div>
            </>
          )}
        </Form.Group>

        <Form.Group className="my-2" controlId="formFooter">
          <input
            type="file"
            multiple
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <>
            <button type="button" className="btn btn-sm bg-white border-1 border-light" onClick={handlePickFile} onMouseDown={(e) => e.preventDefault()}>
              + {t('file', 'Archivo')}
            </button>

            <button onClick={handleSave} onMouseDown={(e) => e.preventDefault()} type="button" className="btn btn-primary btn-sm float-end" disabled={(!currentNote.content && !currentNote.files?.length) || createMutation.isLoading}>
              {createMutation.isLoading && (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              )}
              {t('save_note', 'Guardar nota')}
            </button>
          </>
        </Form.Group>
      </Form>

      <div className="mt-3">
        {consumerNotes && consumerNotes.length === 0 && (
          <div className="text-muted">{t('no_notes', 'Sin notas')}</div>
        )}

        {consumerNotes.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((note, idx, arr) => (
          <div key={note.id || idx}>
            <div className="mb-1">
              <small className="text-muted gap-3 d-flex">
                <div className="text-nowrap">
                  {new Date(note.createdAt).toLocaleString()}
                </div>
                <div className="text-truncate">{note.createdByName || note.createdBy}</div>
                <div className='flex-fill' />
                {note.createdByResourceID === resource?.id && editingNoteId !== note.id && (
                  <div>
                    <button
                      title={t('edit_note', 'Editar nota')}
                     type="button" className="btn btn-link p-0" onClick={() => startEditing(note)}>
                      <IconPencilSquare />
                    </button>
                  </div>
                )}
              </small>
            </div>

            {editingNoteId === note.id ? (
              <div className="mb-2">
                <textarea
                  className="form-control"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={4}
                  style={{ resize: "none" }}
                />
                <div className="mt-2 d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={updateMutation.isLoading}
                    onClick={() => saveEditing(note)}
                  >
                    {updateMutation.isLoading && (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    )}
                    {t('save', 'Guardar')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={updateMutation.isLoading}
                    onClick={cancelEditing}
                  >
                    {t('cancel', 'Cancelar')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mb-1" style={{ whiteSpace: 'pre-wrap', color: '#2c3e50', fontSize: 16 }}>
                {note.content}
              </div>
            )}

            {note.files?.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, calc(50% - 8px))',
                gap: '8px',
                marginBottom: '8px'
              }}>
                {note.files.map((f, i) => (
                  // Bug real (auditoría 2026-08-24, responsive): este <a> es
                  // un grid item (grid-template-columns fijo, ~150px en
                  // 320-375px) sin min-width:0 — a diferencia de su hijo
                  // interno (que sí tiene overflow:hidden propio y por eso
                  // ya se comporta bien como flex item), el propio grid item
                  // no tiene overflow:hidden, así que su tamaño mínimo
                  // automático queda basado en el contenido (el nombre de
                  // archivo completo, sin wrap por el white-space:nowrap de
                  // .text-truncate) y desborda su columna.
                  <a
                    key={note.id + '-file-' + i}
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn border text-start px-1 py-0 rounded"
                    style={{ textDecoration: 'none', backgroundColor: '#EEEFEF', minWidth: 0 }}
                  >
                    <div className="d-flex align-items-center small">
                      <div className="me-1">📄</div>
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontWeight: 700 }} className="text-truncate">{f.name}</div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}

            {idx < arr.length - 1 && <hr className="my-2" />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Notes;